import { defineStore } from "pinia";
import { filters } from "@/store/filters";
import dayjs from "dayjs";
import { getShipments } from "@/lib/api";
import { parseResponseAsShipments } from "@/lib/parseResponseAsShipments";
import { dateComparator, getExportImportPage, getOptionsFromLocalStorage } from "@/lib/utilities";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

export const shipments = defineStore("shipments", {
  state: () => ({
    shipments: [],
    lastPage: null,
    totalCount: null,
    totalCountAll: null,
    shipmentsError: false,
    showSpinner: true,
    totalCountAllLoading: true,
    paginationToken: 1,
    timeFrame: "next_7_days",
    refSearch: ""
  }),
  actions: {
    async getBookingsCount(timeframe) {
      try {
        this.totalCountAllLoading = true;
        const queryParams = {
          ...timeframe,
          page: 1,
          limit: 1,
          taskCode: "0000000000011",
          statusCode: 4
        };
        const response = await getShipments(queryParams);
        this.totalCountAll = Number(response.headers["total-count"]);
      } catch (e) {
        this.totalCountAll = 0;
      } finally {
        this.totalCountAllLoading = false;
      }
    },
    async getBookings() {
      try {
        const shipmentsData = [];
        this.showSpinner = true;
        let queryParams = {
          page: this.paginationToken,
          limit: 25,
          ...filters().getGlobalFilters,
          taskCode: "0000000000011",
          statusCode: 4
        };
        if (getExportImportPage() === "EXP") {
          queryParams = {
            ...queryParams,
            departingFromDateUtc: this.getFromDate(this.timeFrame),
            departingToDateUtc: this.getToDate(this.timeFrame),
            sort: "etdUtcAtPortOfLoading:asc"
          };
        } else {
          queryParams = {
            ...queryParams,
            arrivingFromDateUtc: this.getFromDate(this.timeFrame),
            arrivingToDateUtc: this.getToDate(this.timeFrame),
            sort: "etaUtcAtPortOfDischarge:asc"
          };
        }
        const response = await getShipments(queryParams, "shipments, tasks");
        shipmentsData.push(...response.data);
        this.shipments = parseResponseAsShipments(
          shipmentsData.sort((a, b) => dateComparator(a.etdUtcAtPlaceOfReceipt, b.etdUtcAtPlaceOfReceipt, null))
        );
        this.totalCount = Number(response.headers["total-count"]);
        this.lastPage = Number(response.headers["last-page"]);
      } catch (e) {
        if (e?.status === 404) {
          this.shipments = [];
          this.totalCount = 0;
          this.lastPage = 0;
          this.shipmentsError = false;
        } else {
          this.shipmentsError = true;
        }
      } finally {
        this.showSpinner = false;
      }
    },
    getFromDate(timeFrame) {
      switch (timeFrame) {
        case "last_3_days":
          return dayjs().utc().subtract(3, "days").format("YYYY-MM-DD");
        case "last_7_days":
          return dayjs().utc().subtract(7, "days").format("YYYY-MM-DD");
        case "last_month":
          return dayjs().utc().subtract(1, "month").format("YYYY-MM-DD");
        case "last_3_months":
          return dayjs().utc().subtract(3, "month").format("YYYY-MM-DD");
        default:
          return dayjs().utc().format("YYYY-MM-DD");
      }
    },
    getToDate(timeFrame) {
      switch (timeFrame) {
        case "next_3_days":
          return dayjs().utc().add(3, "days").format("YYYY-MM-DD");
        case "next_7_days":
          return dayjs().utc().add(7, "days").format("YYYY-MM-DD");
        case "next_month":
          return dayjs().utc().add(1, "month").format("YYYY-MM-DD");
        case "next_3_months":
          return dayjs().utc().add(3, "month").format("YYYY-MM-DD");
        default:
          return dayjs().utc().format("YYYY-MM-DD");
      }
    },
    updatePaginationToken(pageToken) {
      this.paginationToken = pageToken;
    },
    updateTimeFrame($event) {
      this.timeFrame = $event.target.value;
      this.paginationToken = 1;
      if (localStorage.getItem("timeFrameSO") !== this.timeFrame) {
        localStorage.setItem("timeFrameSO", this.timeFrame);
      }
    },
    checkTimeFrameFromLocalStorage() {
      const timeFrames = [
        "next_3_days",
        "next_7_days",
        "next_month",
        "next_3_months",
        "last_3_days",
        "last_7_days",
        "last_month",
        "last_3_months"
      ];

      this.timeFrame = getOptionsFromLocalStorage(timeFrames, "timeFrameSO") || "next_7_days";
      return this.timeFrame;
    }
  }
});
